/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.14.0
 */

import type { FC } from 'react';
import { useState } from 'react';

import { useFormikContext } from 'formik';

import { searchIndicator, searchSmallIcon } from '@/main/assets/icons';
import { Input } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import { InputSearch } from '@components/common/InputSearch/InputSearch';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';

import FormField from '../../form/components/FormField';
import type { IFormAddressConfiguration, IFormAddressField } from '../types/formAddressTypes';
import { EFormAddressField } from '../types/formAddressTypes';
import type { TDisabled, TFormAddressField, TOnChange, TOnSelect } from './types';
import { getParentElement, getAddressStringRequestValue } from './utils';

interface IFormAddressFieldOwnProps {
    isLoading?: boolean;
    disabled: TDisabled;
    onChange: TOnChange;
    onSelect: TOnSelect;
}

type TFormAddressFieldProps = TFormAddressField & IFormAddressFieldOwnProps;

const FormAddressField: FC<TFormAddressFieldProps> = ({ isLoading, foundItems, onChange, disabled, ...props }) => {
    const { getFieldProps, setFieldValue, setFieldTouched, values, validateField } =
        useFormikContext<IFormAddressConfiguration>();

    const [onChangeAddressTimeout, setChangeAddressTimeout] = useState(0);

    return (
        <FormField {...props} hideValidation>
            {({ field, label, dataName, required }) => {
                const formField = field.name as EFormAddressField;

                const onChangeHandler = (value: string) => {
                    const formFieldValue: IFormAddressField = { def: value };

                    setFieldValue(formField, formFieldValue, false);
                    setFieldTouched(formField, true, false);

                    if (onChangeAddressTimeout > 0) {
                        clearTimeout(onChangeAddressTimeout);
                    }

                    setChangeAddressTimeout(
                        window.setTimeout(() => {
                            const parentElement = getParentElement(formField, values);

                            const addressSearchParams = {
                                ...parentElement,
                                [formField]: formFieldValue,
                            };

                            onChange(formField, addressSearchParams, {
                                setFieldValue,
                                setFieldTouched,
                            });
                            validateField(formField);
                        }, DEBOUNCE_DEFAULT_DELAY),
                    );
                };

                const onSelectHandler = (value: IInputSearchItem) => {
                    const addressStringRequestValue = getAddressStringRequestValue(value, formField, setFieldValue);

                    props.onSelect(formField, addressStringRequestValue, value.type);
                };

                return (
                    <InputSearch foundItems={foundItems} onChange={onChangeHandler} onSelect={onSelectHandler}>
                        <Input
                            dataName={dataName}
                            disabled={disabled(formField, {
                                getFieldProps,
                            })}
                            kind={EInputKind.normal}
                            label={label}
                            required={required}
                            righticon={isLoading ? searchIndicator : searchSmallIcon}
                            value={field.value?.def || ' '}
                        />
                    </InputSearch>
                );
            }}
        </FormField>
    );
};

export default FormAddressField;
